import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { ApiError, fetchJson } from "@/lib/api";

import type { QuoteInput, QuoteResponse } from "./types";

// POST /api/v1/quote — public stateless quote calculator. The endpoint
// returns 200 with `{ quote: { ok: true, ... } }` on success and 422
// with `{ quote: { ok: false, errors: {...} } }` on validation failure.
// Both are valid quote outcomes for the UI, so we unwrap the 422 body
// instead of letting fetchJson throw. Other error codes (429 from rack
// attack, 404 for a missing type, 500 from the app) propagate as an
// ApiError — react-query surfaces them via `isError`.
//
// NB: a 422 *without* a `quote` key (e.g., a future middleware error
// envelope) re-throws on purpose — we only collapse 422s we recognize
// as Pricing::Quote::Result#as_json.
export async function fetchQuote(input: QuoteInput): Promise<QuoteResponse> {
  try {
    const res = await fetchJson<{ quote: QuoteResponse }>("/api/v1/quote", {
      method: "POST",
      body: input as unknown as Record<string, unknown>
    });
    return res.quote;
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 422 &&
      err.body !== null &&
      typeof err.body === "object" &&
      "quote" in err.body
    ) {
      return (err.body as { quote: QuoteResponse }).quote;
    }
    throw err;
  }
}

// The input shape is part of the cache key so changing any field
// (dates, attachments, options) refetches. Stable JSON.stringify works
// because the keys are typed and ordered.
export function quoteKey(input: QuoteInput): readonly unknown[] {
  return ["public", "quote", input] as const;
}

export function useQuote(
  input: QuoteInput | null
): UseQueryResult<QuoteResponse, ApiError | Error> {
  return useQuery({
    queryKey: input ? quoteKey(input) : ["public", "quote", "none"],
    queryFn: () => fetchQuote(input!),
    enabled: input != null,
    staleTime: 30_000
  });
}
